import type { CodeDirectoryDto, CodeFileDto } from '@flowqa/shared';

export type CoverageDirectoryRow = {
  id: string;
  name: string;
  normalizedPath: string;
  parentPath: string | null;
  tags: string[];
  fileCount: number;
  latestCapturedAt: Date | null;
  linesCovered: number;
  linesTotal: number;
  linesPct: number;
  functionsCovered: number | null;
  functionsTotal: number | null;
  functionsPct: number | null;
  branchesCovered: number | null;
  branchesTotal: number | null;
  branchesPct: number | null;
  statementsCovered: number | null;
  statementsTotal: number | null;
  statementsPct: number | null;
};

const resolveCapturedAt = (file: CodeFileDto): Date =>
  file.latestCapturedAt instanceof Date ? file.latestCapturedAt : file.createdAt;

const compareDatesDesc = (left: Date, right: Date): number => right.getTime() - left.getTime();

const sumNullable = (values: Array<number | null>): number | null => {
  const numericValues = values.filter((value): value is number => value !== null);
  if (numericValues.length === 0) {
    return null;
  }
  return numericValues.reduce((sum, value) => sum + value, 0);
};

const toPct = (covered: number | null, total: number | null): number | null => {
  if (covered === null || total === null || total <= 0) {
    return null;
  }
  return (covered / total) * 100;
};

export const buildLatestCoverageFiles = (files: CodeFileDto[]): CodeFileDto[] =>
  [...files].sort((left, right) =>
    left.normalizedPath.localeCompare(right.normalizedPath, undefined, {
      sensitivity: 'base',
    }),
  );

const resolveDirectoryName = (normalizedPath: string): string => {
  const segments = normalizedPath.split('/').filter((segment) => segment.length > 0);
  return segments[segments.length - 1] ?? normalizedPath;
};

const resolveParentPath = (normalizedPath: string): string | null => {
  const lastSeparatorIndex = normalizedPath.lastIndexOf('/');
  if (lastSeparatorIndex <= 0) {
    return null;
  }
  return normalizedPath.slice(0, lastSeparatorIndex);
};

export const buildCoverageDirectoryRows = (
  directories: CodeDirectoryDto[],
  files: CodeFileDto[],
): CoverageDirectoryRow[] =>
  directories
    .map((directory) => {
      const directoryFiles = files.filter((file) =>
        file.normalizedPath.startsWith(`${directory.normalizedPath}/`),
      );
      const latestCapturedAt =
        directoryFiles.map((file) => resolveCapturedAt(file)).sort(compareDatesDesc)[0] ?? null;
      const linesCovered = directoryFiles.reduce((sum, file) => sum + (file.linesCovered ?? 0), 0);
      const linesTotal = directoryFiles.reduce((sum, file) => sum + (file.linesTotal ?? 0), 0);
      const functionsCovered = sumNullable(
        directoryFiles.map((file) => file.functionsCovered ?? null),
      );
      const functionsTotal = sumNullable(directoryFiles.map((file) => file.functionsTotal ?? null));
      const branchesCovered = sumNullable(
        directoryFiles.map((file) => file.branchesCovered ?? null),
      );
      const branchesTotal = sumNullable(directoryFiles.map((file) => file.branchesTotal ?? null));
      const statementsCovered = sumNullable(
        directoryFiles.map((file) => file.statementsCovered ?? null),
      );
      const statementsTotal = sumNullable(
        directoryFiles.map((file) => file.statementsTotal ?? null),
      );

      return {
        id: directory.id,
        name: directory.name,
        normalizedPath: directory.normalizedPath,
        parentPath: directory.parentDirectoryPath ?? null,
        tags: directory.tags,
        fileCount: directoryFiles.length,
        latestCapturedAt,
        linesCovered,
        linesTotal,
        linesPct: linesTotal > 0 ? (linesCovered / linesTotal) * 100 : 0,
        functionsCovered,
        functionsTotal,
        functionsPct: toPct(functionsCovered, functionsTotal),
        branchesCovered,
        branchesTotal,
        branchesPct: toPct(branchesCovered, branchesTotal),
        statementsCovered,
        statementsTotal,
        statementsPct: toPct(statementsCovered, statementsTotal),
      };
    })
    .filter((directory) => directory.fileCount > 0)
    .sort((left, right) =>
      left.normalizedPath.localeCompare(right.normalizedPath, undefined, {
        sensitivity: 'base',
      }),
    );

export const mapCodeDirectoryDtosToCoverageRows = (
  directories: CodeDirectoryDto[],
): CoverageDirectoryRow[] =>
  directories.map((directory) => ({
    id: directory.id,
    name: directory.name,
    normalizedPath: directory.normalizedPath,
    parentPath: directory.parentDirectoryPath ?? null,
    tags: directory.tags,
    fileCount: directory.fileCount ?? 0,
    latestCapturedAt: directory.latestCapturedAt ?? null,
    linesCovered: directory.linesCovered ?? 0,
    linesTotal: directory.linesTotal ?? 0,
    linesPct: directory.linesPct ?? 0,
    functionsCovered: directory.functionsCovered ?? null,
    functionsTotal: directory.functionsTotal ?? null,
    functionsPct: directory.functionsPct ?? null,
    branchesCovered: directory.branchesCovered ?? null,
    branchesTotal: directory.branchesTotal ?? null,
    branchesPct: directory.branchesPct ?? null,
    statementsCovered: directory.statementsCovered ?? null,
    statementsTotal: directory.statementsTotal ?? null,
    statementsPct: directory.statementsPct ?? null,
  }));

export const buildCoverageDirectoryRowsFromFiles = (
  files: CodeFileDto[],
): CoverageDirectoryRow[] => {
  const directoryMap = new Map<
    string,
    {
      id: string;
      name: string;
      normalizedPath: string;
      parentPath: string | null;
      tags: Set<string>;
      files: CodeFileDto[];
    }
  >();

  for (const file of files) {
    const directoryId = file.directoryId;
    const directoryPath = file.directoryPath;
    if (
      directoryId === null ||
      directoryId === undefined ||
      directoryPath === null ||
      directoryPath === undefined ||
      directoryPath.length === 0
    ) {
      continue;
    }

    const existingDirectory = directoryMap.get(directoryId);
    if (existingDirectory) {
      existingDirectory.files.push(file);
      for (const tag of file.tags) {
        existingDirectory.tags.add(tag);
      }
      continue;
    }

    directoryMap.set(directoryId, {
      id: directoryId,
      name: resolveDirectoryName(directoryPath),
      normalizedPath: directoryPath,
      parentPath: resolveParentPath(directoryPath),
      tags: new Set(file.tags),
      files: [file],
    });
  }

  return Array.from(directoryMap.values())
    .map((directory) => {
      const latestCapturedAt =
        directory.files.map((file) => resolveCapturedAt(file)).sort(compareDatesDesc)[0] ?? null;
      const linesCovered = directory.files.reduce((sum, file) => sum + (file.linesCovered ?? 0), 0);
      const linesTotal = directory.files.reduce((sum, file) => sum + (file.linesTotal ?? 0), 0);
      const functionsCovered = sumNullable(
        directory.files.map((file) => file.functionsCovered ?? null),
      );
      const functionsTotal = sumNullable(
        directory.files.map((file) => file.functionsTotal ?? null),
      );
      const branchesCovered = sumNullable(
        directory.files.map((file) => file.branchesCovered ?? null),
      );
      const branchesTotal = sumNullable(directory.files.map((file) => file.branchesTotal ?? null));
      const statementsCovered = sumNullable(
        directory.files.map((file) => file.statementsCovered ?? null),
      );
      const statementsTotal = sumNullable(
        directory.files.map((file) => file.statementsTotal ?? null),
      );

      return {
        id: directory.id,
        name: directory.name,
        normalizedPath: directory.normalizedPath,
        parentPath: directory.parentPath,
        tags: Array.from(directory.tags).sort((left, right) =>
          left.localeCompare(right, undefined, { sensitivity: 'base' }),
        ),
        fileCount: directory.files.length,
        latestCapturedAt,
        linesCovered,
        linesTotal,
        linesPct: linesTotal > 0 ? (linesCovered / linesTotal) * 100 : 0,
        functionsCovered,
        functionsTotal,
        functionsPct: toPct(functionsCovered, functionsTotal),
        branchesCovered,
        branchesTotal,
        branchesPct: toPct(branchesCovered, branchesTotal),
        statementsCovered,
        statementsTotal,
        statementsPct: toPct(statementsCovered, statementsTotal),
      };
    })
    .sort((left, right) =>
      left.normalizedPath.localeCompare(right.normalizedPath, undefined, {
        sensitivity: 'base',
      }),
    );
};

export const buildDirectoryPathHref = (id: string): string => `/coverage-directories/${id}`;
